import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { safeRedirectPath } from '../../App';
import './Auth.css';

interface LoginFormData {
  email: string;
  password: string;
}

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Redirect target after login. Callers pass an in-app path via ?redirect=;
  // safeRedirectPath validates it (rejects protocol-relative, external
  // schemes, unsafe chars) and returns a single-slash absolute path or
  // null. Fall back to the Notches Dashboard (default landing) when absent or rejected.
  const redirectTo = safeRedirectPath(searchParams.get('redirect')) || '/notches/dashboard';
  const { login, verify2FA } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [preAuthToken, setPreAuthToken] = useState<string>('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      await login(data.email, data.password);
      navigate(redirectTo);
    } catch (err: any) {
      if (err.requires2FA) {
        setPreAuthToken(err.preAuthToken);
        setShowTwoFactor(true);
      } else if (err.message.includes('verified')) {
        navigate('/verify-email');
      } else {
        setError(err.message || 'Invalid email or password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await verify2FA(twoFactorCode, preAuthToken);
      navigate(redirectTo);
    } catch (err: any) {
      setError(err.message || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  if (showTwoFactor) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div className="auth-logo-w">W</div>
          </div>
          <div className="verification-icon">🔐</div>
          <h2>Check Your Email</h2>
          <p className="subtitle">Enter the 6-digit code we sent to your email address</p>

          {error && <div className="error-alert">{error}</div>}

          <form onSubmit={onSubmit2FA}>
            <div className="form-group">
              <label htmlFor="twoFactorCode">Verification Code</label>
              <input
                type="text"
                id="twoFactorCode"
                value={twoFactorCode}
                onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                style={{ textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.5rem' }}
                autoComplete="one-time-code"
                autoFocus
              />
            </div>

            <button type="submit" className="btn-primary" disabled={isLoading || twoFactorCode.length !== 6}>
              {isLoading ? 'Verifying...' : 'Verify & Sign In'}
            </button>
          </form>

          <div className="auth-footer">
            <button className="btn-link" onClick={() => { setShowTwoFactor(false); setError(null); setTwoFactorCode(''); }}>
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div className="auth-logo-w">W</div>
        </div>
        <h2>Welcome Back</h2>
        <p className="subtitle">Sign in to access your dashboard</p>

        {error && <div className="error-alert">{error}</div>}

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              className={errors.email ? 'error' : ''}
              placeholder="you@example.com"
            />
            {errors.email && (
              <span className="error-message">{errors.email.message}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password
              <Link to="/forgot-password" className="forgot-link">
                Forgot password?
              </Link>
            </label>
            <input
              type="password"
              id="password"
              {...register('password', {
                required: 'Password is required',
              })}
              className={errors.password ? 'error' : ''}
              placeholder="Enter your password"
            />
            {errors.password && (
              <span className="error-message">{errors.password.message}</span>
            )}
          </div>

          <button type="submit" className="btn-primary" disabled={isLoading}>
            {isLoading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Don't have an account?{' '}
            <Link to="/register" className="link">
              Create Account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};